/**
 * NHI (National Health Number) citizen registration wizard.
 *
 * Faithful React port of James's approved prototype
 * (syrian_nhi_registration_prototype_final.html). Governed behaviours carried
 * over from the prototype:
 *  - 4-step wizard (Identity & Personal / Civil Registry & Family / Contact &
 *    Residence / Review & Confirmation) inside a 430px mobile frame.
 *  - Step 1 identity verification sequence: civil registry check → strong
 *    identifiers → EMPI demographic match (animated). A definite match routes
 *    to the "Existing NHN Found" screen; no second NHN is issued.
 *  - No-ID branch: extended-verification banner + alternative document fields;
 *    identity stays unverified (IAL-1) and the wizard continues.
 *  - Step 3 OTP send/verify + residence address.
 *  - Step 4 review cards with edit-back links, declaration + data-processing
 *    checkboxes, then an animated submission sequence (documentary
 *    verification → EMPI duplicate search → outcome classification → NHN
 *    generation / identity resolution routing) before the result screen.
 *  - Result screens: Existing NHN Found / Registration Pending Review (ref
 *    number) / Registration Successful (NHN display SY-####-####-##, QR,
 *    assurance chip, permanence note, print/save buttons).
 *
 * The wizard POSTs the collected data to Dex's pinned contract
 * `POST /api/v1/nhi/register` (see ../api/nhi.ts) and renders whatever outcome
 * the server returns:
 *   existing       → definite EMPI match: show the existing NHN, no new number
 *   pending_review → possible duplicate: identity resolution queue, ref shown
 *   success        → new NHN issued (permanent; verification improves only)
 *
 * While the backend is landing (or unreachable — e.g. pages.dev demo), the
 * submit falls back to the prototype's local scenario behaviour so the flow
 * stays demonstrable. The dev-only `?scenario=new|existing|duplicate` query
 * param additionally pins the demo outcome and reveals the scenario selector.
 *
 * All UI strings come from the app-wide i18n store (`nhi.*` keys) — no
 * hardcoded bilingual spans. RTL is inherited from `document.dir` which the
 * i18n store keeps in sync.
 */
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { t as appT } from '../lib/i18n'
import {
  postNhiRegister,
  type NhiOutcome,
  type NhiRegisterPayload,
  type NhiRegisterResponse,
} from '../api/nhi'
import { appendOfflineRegistration, type OfflineRegistration } from '../lib/offlineStore'
import '../nhi-wizard.css'

/* ============================================================ Types */

export type NhiScenario = 'new' | 'existing' | 'duplicate'
type View = 'wizard' | 'existing' | 'pendingReview' | 'success'

interface NhiFormData {
  hasID: 'yes' | 'no' | null
  nationalID: string
  familyName: string
  firstName: string
  fatherName: string
  motherName: string
  gender: string
  dob: string
  placeOfBirth: string
  identityVerified: boolean
  assurance: string | null
  civilGov: string
  placeOfReg: string
  civilRegNo: string
  maritalStatus: string
  docType: string
  docNumber: string
  docIssuer: string
  mobile2: string
  otpSent: boolean
  otpVerified: boolean
  otp: string
  email: string
  governorate: string
  city: string
  neighborhood: string
  street: string
  buildingNo: string
  declare: boolean
  dataProcessing: boolean
}

const INITIAL_DATA: NhiFormData = {
  hasID: null,
  nationalID: '',
  familyName: '',
  firstName: '',
  fatherName: '',
  motherName: '',
  gender: '',
  dob: '',
  placeOfBirth: '',
  identityVerified: false,
  assurance: null,
  civilGov: '',
  placeOfReg: '',
  civilRegNo: '',
  maritalStatus: '',
  docType: '',
  docNumber: '',
  docIssuer: '',
  mobile2: '',
  otpSent: false,
  otpVerified: false,
  otp: '',
  email: '',
  governorate: '',
  city: '',
  neighborhood: '',
  street: '',
  buildingNo: '',
  declare: false,
  dataProcessing: false,
}

const GOVERNORATES = [
  'Damascus', 'Aleppo', 'Homs', 'Hama', 'Lattakia', 'Tartus', 'Idlib',
  'Daraa', 'As-Suwayda', 'Quneitra', 'Deir ez-Zor', 'Raqqa', 'Hasakah',
  'Rif Dimashq',
]

const NHI_EN: Record<string, string> = {
  'nhi.step.personal': 'Personal',
  'nhi.step.civil': 'Civil & Family',
  'nhi.step.contact': 'Contact',
  'nhi.step.review': 'Review',
  'nhi.step.issued': 'NHN Issued',
  'nhi.stepperAria': 'Registration steps',
  'nhi.stepOf': 'Step {n} of 4',
  'nhi.qrAria': 'National Health Number QR code',
  'nhi.select': 'Select',
  'nhi.yes': 'Yes',
  'nhi.no': 'No',
  'nhi.err.required': 'Required.',
  'nhi.err.selectOption': 'Please select an option.',
  'nhi.err.nationalId': 'National ID number is required.',
  'nhi.err.declare': 'Please confirm the declaration.',
  'nhi.err.dataProcessing': 'Please acknowledge data processing.',
  'nhi.hasId': 'Do you have a Syrian National ID?',
  'nhi.nationalId': 'National ID Number',
  'nhi.ph.nationalId': 'Enter National ID number',
  'nhi.altDocBanner': 'If no National ID is available or electronic verification fails, the application moves to the Extended Identity Verification route using demographic matching instead. You may provide an alternative document below.',
  'nhi.docType': 'Other Document Type',
  'nhi.doc.passport': 'Passport',
  'nhi.doc.extract': 'Civil Extract',
  'nhi.doc.other': 'Other',
  'nhi.docNumber': 'Document Number',
  'nhi.ph.docNumber': 'Enter document number',
  'nhi.docIssuer': 'Document Issuer / Country',
  'nhi.ph.docIssuer': 'Enter issuer',
  'nhi.s1.title': 'Identity & Personal Information',
  'nhi.s1.helper': 'Start with your National ID if available, to verify identity and search for an existing NHN, then complete your personal details.',
  'nhi.s1.personalDetails': 'Personal Details',
  'nhi.familyName': 'Family Name',
  'nhi.firstName': 'First Name',
  'nhi.fatherName': "Father's Name",
  'nhi.motherName': "Mother's Name",
  'nhi.ph.motherName': "Enter mother's name",
  'nhi.gender': 'Gender',
  'nhi.gender.male': 'Male',
  'nhi.gender.female': 'Female',
  'nhi.dob': 'Date of Birth',
  'nhi.placeOfBirth': 'Place of Birth',
  'nhi.badge.civilVerified': 'Civil Registry verified',
  'nhi.vstep.civil': 'Checking Civil Registry',
  'nhi.vstep.strongIds': 'Searching strong identifiers',
  'nhi.vstep.empi': 'Running EMPI demographic match',
  'nhi.s2.title': 'Civil Registry & Family Information',
  'nhi.s2.helper': 'This information helps distinguish similar records and strengthens identity assurance.',
  'nhi.civilGov': 'Civil Registry Governorate',
  'nhi.placeOfReg': 'Place of Registration',
  'nhi.ph.placeOfReg': 'Enter place of registration',
  'nhi.civilRegNo': 'Civil Registration Number',
  'nhi.ph.civilRegNo': 'Enter civil registration number',
  'nhi.maritalStatus': 'Marital Status',
  'nhi.marital.single': 'Single',
  'nhi.marital.married': 'Married',
  'nhi.marital.divorced': 'Divorced',
  'nhi.marital.widowed': 'Widowed',
  'nhi.s3.title': 'Contact & Residence',
  'nhi.s3.helper': 'A mobile number is desirable for OTP and notifications but is not an absolute prerequisite for receiving an NHN.',
  'nhi.s3.address': 'Residence Address',
  'nhi.email': 'Email',
  'nhi.ph.optional': 'Optional',
  'nhi.mobile': 'Mobile Number',
  'nhi.ph.mobile': '7XX XXX XXX',
  'nhi.sendOtp': 'Send OTP',
  'nhi.resend': 'Resend',
  'nhi.verifyOtp': 'Verify code',
  'nhi.otpCode': 'OTP Verification Code',
  'nhi.badge.verified': 'Verified',
  'nhi.governorate': 'Governorate',
  'nhi.city': 'City / District',
  'nhi.ph.city': 'Enter city or district',
  'nhi.neighborhood': 'Neighborhood / Village',
  'nhi.ph.neighborhood': 'Enter neighborhood or village',
  'nhi.street': 'Street',
  'nhi.ph.street': 'Enter street name',
  'nhi.buildingNo': 'Building Number',
  'nhi.ph.buildingNo': 'Enter building number',
  'nhi.s4.title': 'Review & Confirmation',
  'nhi.s4.helper': 'Please review the information below before confirming registration.',
  'nhi.duplicateBanner': 'Your details may match an existing record. This registration will be sent for identity review before any new NHN is issued.',
  'nhi.rv.fullName': 'Full Name',
  'nhi.rv.registryGov': 'Registry Governorate',
  'nhi.rv.city': 'City',
  'nhi.rv.buildingNo': 'Building No.',
  'nhi.rv.mobileVerification': 'Mobile Verification',
  'nhi.rv.verificationStatus': 'Verification Status',
  'nhi.verified': 'Verified',
  'nhi.unverified': 'Unverified',
  'nhi.notProvided': 'Not provided',
  'nhi.edit': 'Edit',
  'nhi.editStepAria': 'Edit {title}',
  'nhi.declareText': 'I confirm that the information above is correct and complete.',
  'nhi.dataProcText': 'I acknowledge processing under the National Health Data Policy.',
  'nhi.sstep.docVerification': 'Civil / documentary verification',
  'nhi.sstep.empiSearch': 'Searching EMPI for duplicates',
  'nhi.sstep.classify': 'Classifying match outcome',
  'nhi.sstep.routeQueue': 'Routing to identity resolution queue',
  'nhi.sstep.generateNhn': 'Generating National Health Number',
  'nhi.existing.title': 'Existing NHN Found',
  'nhi.existing.helper': 'The EMPI match found a definite existing identity for you. A second NHN cannot be issued for the same person.',
  'nhi.existing.note': 'Per governance rules: the registry is always searched before a new NHN is created, and this result came from strong identifiers, not name matching alone.',
  'nhi.pending.title': 'Registration Pending Review',
  'nhi.pending.helper': 'Your details may match an existing record. This case has been routed to identity resolution for manual review before any NHN is issued.',
  'nhi.pending.note': "You'll be notified by SMS or email once the review is complete.",
  'nhi.success.title': 'Registration Successful',
  'nhi.success.yourNhn': 'Your National Health Number',
  'nhi.success.permanence': 'This number is permanent for life — any future identity verification improves your assurance level only, never this number.',
  'nhi.verifiedChip': 'Identity Verified',
  'nhi.basicChip': 'Registered — verification pending',
  'nhi.pendingChip': 'Pending Review',
  'nhi.printCert': 'Print Certificate',
  'nhi.savePdf': 'Save PDF',
  'nhi.goHome': 'Go to Home',
  'nhi.devScenario': 'Prototype scenario',
  'nhi.scenario.new': 'New citizen',
  'nhi.scenario.existing': 'Existing NHN found',
  'nhi.scenario.duplicate': 'Possible duplicate',
  'nhi.back': 'Back',
  'nhi.next': 'Save & Continue',
  'nhi.verifyContinue': 'Verify Identity & Continue',
  'nhi.continueNoId': 'Continue without National ID',
  'nhi.confirmReg': 'Confirm Registration',
}

const NHI_AR: Record<string, string> = {
  'nhi.step.personal': 'الشخصية والهوية',
  'nhi.step.civil': 'السجل والأسرة',
  'nhi.step.contact': 'الاتصال',
  'nhi.step.review': 'التأكيد',
  'nhi.step.issued': 'صدور الرقم',
  'nhi.stepperAria': 'خطوات التسجيل',
  'nhi.stepOf': 'الخطوة {n} من ٤',
  'nhi.qrAria': 'رمز الاستجابة السريعة للرقم الصحي الوطني',
  'nhi.select': 'اختر',
  'nhi.yes': 'نعم',
  'nhi.no': 'لا',
  'nhi.err.required': 'مطلوب.',
  'nhi.err.selectOption': 'يرجى اختيار أحد الخيارات.',
  'nhi.err.nationalId': 'الرقم الوطني مطلوب.',
  'nhi.err.declare': 'يرجى تأكيد الإقرار.',
  'nhi.err.dataProcessing': 'يرجى تأكيد معالجة البيانات.',
  'nhi.hasId': 'هل لديك رقم وطني سوري؟',
  'nhi.nationalId': 'الرقم الوطني',
  'nhi.ph.nationalId': 'أدخل الرقم الوطني',
  'nhi.altDocBanner': 'إذا لم يتوفر رقم وطني أو فشل التحقق الإلكتروني، ينتقل الطلب إلى مسار التحقق الموسّع باستخدام المطابقة الديموغرافية. يمكنك تقديم وثيقة بديلة أدناه.',
  'nhi.docType': 'نوع وثيقة تعريف أخرى',
  'nhi.doc.passport': 'جواز سفر',
  'nhi.doc.extract': 'مستخرج مدني',
  'nhi.doc.other': 'أخرى',
  'nhi.docNumber': 'رقم الوثيقة',
  'nhi.ph.docNumber': 'أدخل رقم الوثيقة',
  'nhi.docIssuer': 'جهة الإصدار / الدولة',
  'nhi.ph.docIssuer': 'أدخل جهة الإصدار',
  'nhi.s1.title': 'الهوية والمعلومات الشخصية',
  'nhi.s1.helper': 'ابدأ برقمك الوطني إن توفر للتحقق من الهوية والبحث عن رقم صحي وطني موجود مسبقاً، ثم أكمل معلوماتك الشخصية.',
  'nhi.s1.personalDetails': 'المعلومات الشخصية',
  'nhi.familyName': 'اسم العائلة',
  'nhi.firstName': 'الاسم الأول',
  'nhi.fatherName': 'اسم الأب',
  'nhi.motherName': 'اسم الأم',
  'nhi.ph.motherName': 'أدخل اسم الأم',
  'nhi.gender': 'الجنس',
  'nhi.gender.male': 'ذكر',
  'nhi.gender.female': 'أنثى',
  'nhi.dob': 'تاريخ الميلاد',
  'nhi.placeOfBirth': 'مكان الولادة',
  'nhi.badge.civilVerified': 'موثّق من السجل المدني',
  'nhi.vstep.civil': 'التحقق من السجل المدني',
  'nhi.vstep.strongIds': 'البحث عن معرّفات قوية',
  'nhi.vstep.empi': 'تشغيل مطابقة السجل الرئيسي للمرضى',
  'nhi.s2.title': 'معلومات السجل والأسرة',
  'nhi.s2.helper': 'هذه المعلومات تساعد على التمييز بين السجلات المتشابهة وتعزيز موثوقية الهوية.',
  'nhi.civilGov': 'المحافظة / أمانة السجل المدني',
  'nhi.placeOfReg': 'مكان القيد',
  'nhi.ph.placeOfReg': 'أدخل مكان القيد',
  'nhi.civilRegNo': 'رقم القيد المدني',
  'nhi.ph.civilRegNo': 'أدخل رقم القيد المدني',
  'nhi.maritalStatus': 'الحالة الاجتماعية',
  'nhi.marital.single': 'أعزب',
  'nhi.marital.married': 'متزوج',
  'nhi.marital.divorced': 'مطلّق',
  'nhi.marital.widowed': 'أرمل',
  'nhi.s3.title': 'معلومات الاتصال والإقامة',
  'nhi.s3.helper': 'رقم الهاتف المحمول مفيد لرمز التحقق والإشعارات، لكنه ليس شرطاً أساسياً للحصول على الرقم الصحي الوطني.',
  'nhi.s3.address': 'عنوان الإقامة',
  'nhi.email': 'البريد الإلكتروني',
  'nhi.ph.optional': 'اختياري',
  'nhi.mobile': 'رقم الهاتف المحمول',
  'nhi.ph.mobile': '7XX XXX XXX',
  'nhi.sendOtp': 'إرسال رمز التحقق',
  'nhi.resend': 'إعادة الإرسال',
  'nhi.verifyOtp': 'تحقق من الرمز',
  'nhi.otpCode': 'رمز التحقق',
  'nhi.badge.verified': 'تم التحقق',
  'nhi.governorate': 'المحافظة',
  'nhi.city': 'المدينة / المنطقة',
  'nhi.ph.city': 'أدخل المدينة أو المنطقة',
  'nhi.neighborhood': 'الحي / القرية',
  'nhi.ph.neighborhood': 'أدخل الحي أو القرية',
  'nhi.street': 'الشارع',
  'nhi.ph.street': 'أدخل اسم الشارع',
  'nhi.buildingNo': 'رقم البناء',
  'nhi.ph.buildingNo': 'أدخل رقم البناء',
  'nhi.s4.title': 'مراجعة وتأكيد المعلومات',
  'nhi.s4.helper': 'يرجى مراجعة المعلومات أدناه قبل تأكيد التسجيل.',
  'nhi.duplicateBanner': 'قد تتطابق بياناتك مع سجل موجود مسبقاً. سيتم إرسال الطلب لمراجعة الهوية قبل إصدار رقم صحي جديد.',
  'nhi.rv.fullName': 'الاسم الكامل',
  'nhi.rv.registryGov': 'محافظة السجل',
  'nhi.rv.city': 'المدينة',
  'nhi.rv.buildingNo': 'رقم البناء',
  'nhi.rv.mobileVerification': 'التحقق من الهاتف',
  'nhi.rv.verificationStatus': 'حالة التحقق',
  'nhi.verified': 'موثّق',
  'nhi.unverified': 'غير موثّق',
  'nhi.notProvided': 'غير متوفر',
  'nhi.edit': 'تعديل',
  'nhi.editStepAria': 'تعديل {title}',
  'nhi.declareText': 'أقرّ بأن المعلومات أعلاه صحيحة وكاملة.',
  'nhi.dataProcText': 'أوافق على معالجة بياناتي وفق سياسة البيانات الصحية الوطنية.',
  'nhi.sstep.docVerification': 'التحقق من الوثائق المدنية',
  'nhi.sstep.empiSearch': 'البحث في السجل الرئيسي عن تكرار',
  'nhi.sstep.classify': 'تصنيف نتيجة المطابقة',
  'nhi.sstep.routeQueue': 'التحويل لقائمة مراجعة الهوية',
  'nhi.sstep.generateNhn': 'إصدار الرقم الصحي الوطني',
  'nhi.existing.title': 'تم العثور على رقم صحي وطني موجود',
  'nhi.existing.helper': 'أظهرت مطابقة السجل الرئيسي أن لديك رقماً صحياً وطنياً مسجلاً مسبقاً. لا يمكن إصدار رقم جديد لنفس الشخص.',
  'nhi.existing.note': 'وفق قواعد النظام: البحث قبل التسجيل إلزامي دائماً، وتم اعتماد هذه النتيجة عبر معرّفات قوية وليس الاسم وحده.',
  'nhi.pending.title': 'الطلب قيد المراجعة',
  'nhi.pending.helper': 'قد تتطابق معلوماتك مع سجل موجود مسبقاً. تم تحويل الطلب إلى مراجعة الهوية قبل إصدار رقم صحي وطني.',
  'nhi.pending.note': 'سيتم إشعارك عبر الرسائل النصية أو البريد الإلكتروني فور اكتمال المراجعة.',
  'nhi.success.title': 'تم التسجيل بنجاح',
  'nhi.success.yourNhn': 'رقمك الصحي الوطني',
  'nhi.success.permanence': 'هذا الرقم دائم مدى الحياة. أي تحقق لاحق يحسّن مستوى الثقة فقط ولا يغيّر الرقم.',
  'nhi.verifiedChip': 'الهوية موثّقة',
  'nhi.basicChip': 'مسجّل — التحقق قيد التحسين',
  'nhi.pendingChip': 'قيد المراجعة',
  'nhi.printCert': 'طباعة الشهادة',
  'nhi.savePdf': 'حفظ PDF',
  'nhi.goHome': 'العودة للرئيسية',
  'nhi.devScenario': 'سيناريو النموذج',
  'nhi.scenario.new': 'مواطن جديد',
  'nhi.scenario.existing': 'تم العثور على رقم موجود',
  'nhi.scenario.duplicate': 'احتمال تكرار',
  'nhi.back': 'رجوع',
  'nhi.next': 'حفظ ومتابعة',
  'nhi.verifyContinue': 'تحقق ومتابعة',
  'nhi.continueNoId': 'المتابعة بدون رقم وطني',
  'nhi.confirmReg': 'تأكيد التسجيل',
}

const GOV_AR: Record<string, string> = {
  Damascus: 'دمشق', Aleppo: 'حلب', Homs: 'حمص', Hama: 'حماة', Lattakia: 'اللاذقية', Tartus: 'طرطوس', Idlib: 'إدلب', Daraa: 'درعا', 'As-Suwayda': 'السويداء', Quneitra: 'القنيطرة', 'Deir ez-Zor': 'دير الزور', Raqqa: 'الرقة', Hasakah: 'الحسكة', 'Rif Dimashq': 'ريف دمشق',
}

function t(key: string): string {
  return NHI_EN[key] ?? appT(key)
}
function ar(key: string): string {
  if (key.startsWith('nhi.gov.')) return GOV_AR[key.slice('nhi.gov.'.length)] ?? key.slice('nhi.gov.'.length)
  return NHI_AR[key] ?? key
}
function Bi({ k }: { k: string }) {
  return (
    <span className="nhi-bi">
      <span className="nhi-en">{t(k)}</span>
      <span className="nhi-ar" dir="rtl">{ar(k)}</span>
    </span>
  )
}

/** Steps 1–4 are the interactive wizard; 5 is the success result screen. */
const STEPPER_STEPS = [
  { id: 1, key: 'nhi.step.personal' },
  { id: 2, key: 'nhi.step.civil' },
  { id: 3, key: 'nhi.step.contact' },
  { id: 4, key: 'nhi.step.review' },
  { id: 5, key: 'nhi.step.issued' },
]

const VALID_SCENARIOS: readonly NhiScenario[] = ['new', 'existing', 'duplicate']

/**
 * Read the dev-only scenario from `?scenario=` (hidden toggle per spec).
 * Returns null when the param is absent — the selector UI is hidden then, and
 * the wizard always calls the real API with outcome-driven rendering.
 */
export function scenarioFromQuery(search: string): NhiScenario | null {
  const value = new URLSearchParams(search).get('scenario')
  return value && (VALID_SCENARIOS as readonly string[]).includes(value)
    ? (value as NhiScenario)
    : null
}

/* ============================================================ NHN/QR */

function checkDigit(digits: string): string {
  let sum = 0
  for (let i = 0; i < digits.length; i++) sum += parseInt(digits[i], 10) * ((i % 6) + 2)
  return String(sum % 97).padStart(2, '0')
}
/** Local NHN format fallback (SY-####-####-##) when the API is unreachable. */
function genLocalNhn(): string {
  const g1 = String(Math.floor(1000 + Math.random() * 9000))
  const g2 = String(Math.floor(1000 + Math.random() * 9000))
  const g3 = checkDigit(g1 + g2) + String(Math.floor(1 + Math.random() * 9))
  return `SY-${g1}-${g2}-0${g3}`
}
function genLocalRef(): string {
  return 'REV-' + String(Math.floor(100000 + Math.random() * 900000))
}

/** Deterministic pseudo-QR pattern (same generator as the prototype). */
function qrRects(seed: string): ReactNode {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  const n = 11
  const rects: ReactNode[] = []
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      h = (h * 1103515245 + 12345) >>> 0
      const on = h % 100 < 46
      const isFinder = (x < 3 && y < 3) || (x > n - 4 && y < 3) || (x < 3 && y > n - 4)
      if (on || isFinder) {
        rects.push(
          <rect key={`${x}-${y}`} x={x * 10} y={y * 10} width={9} height={9} fill="#063B32" />,
        )
      }
    }
  }
  return (
    <svg
      viewBox={`0 0 ${n * 10} ${n * 10}`}
      width="100%"
      height="100%"
      role="img"
      aria-label={t('nhi.qrAria')}
    >
      {rects}
    </svg>
  )
}

function maskId(id: string): string {
  if (!id) return ''
  if (id.length <= 4) return id
  return '••••' + id.slice(-4)
}

/* ============================================================ Component */

export function NhiRegistrationWizard({ locationSearch }: { locationSearch: string }) {
  const forcedScenario = useMemo(() => scenarioFromQuery(locationSearch), [locationSearch])
  // Dev selector overrides the param live; defaults to the param (or 'new').
  const [scenario, setScenario] = useState<NhiScenario>(forcedScenario ?? 'new')
  useEffect(() => {
    if (forcedScenario) setScenario(forcedScenario)
  }, [forcedScenario])

  const [view, setView] = useState<View>('wizard')
  const [step, setStep] = useState(1)
  const [maxReached, setMaxReached] = useState(1)
  const [verifying, setVerifying] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  // Animated progress index for the verification / submission sequences
  // (drives active/done classes; -1 = nothing started yet).
  const [verifyIdx, setVerifyIdx] = useState(-1)
  const [submitIdx, setSubmitIdx] = useState(-1)
  const [data, setData] = useState<NhiFormData>({ ...INITIAL_DATA })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [nhn, setNhn] = useState<string | null>(null)
  const [refNumber, setRefNumber] = useState<string | null>(null)
  const [resultAssurance, setResultAssurance] = useState<string | null>(null)
  const timers = useRef<number[]>([])

  const clearTimers = useCallback(() => {
    timers.current.forEach((id) => window.clearTimeout(id))
    timers.current = []
  }, [])
  useEffect(() => clearTimers, [clearTimers])

  const setField = useCallback(
    (key: keyof NhiFormData, value: NhiFormData[keyof NhiFormData]) => {
      setData((prev) => ({ ...prev, [key]: value }))
      setErrors((prev) => {
        if (!(key in prev)) return prev
        const next = { ...prev }
        delete next[key]
        return next
      })
    },
    [],
  )

  /* ---------------- validation ---------------- */
  function validateStep(n: number): boolean {
    const errs: Record<string, string> = {}
    if (n === 1) {
      if (!data.firstName.trim()) errs.firstName = t('nhi.err.required')
      if (!data.fatherName.trim()) errs.fatherName = t('nhi.err.required')
      if (!data.familyName.trim()) errs.familyName = t('nhi.err.required')
      if (!data.motherName.trim()) errs.motherName = t('nhi.err.required')
      if (!data.gender) errs.gender = t('nhi.err.required')
      if (!data.dob) errs.dob = t('nhi.err.required')
      if (!data.placeOfBirth) errs.placeOfBirth = t('nhi.err.required')
      if (data.hasID === null) errs.hasID = t('nhi.err.selectOption')
      if (data.hasID === 'yes' && !/^\d{11}$/.test(data.nationalID.trim())) errs.nationalID = t('nhi.err.nationalId')
    }
    if (n === 2) {
      if (!data.civilGov) errs.civilGov = t('nhi.err.required')
    }
    if (n === 3) {
      if (!data.governorate) errs.governorate = t('nhi.err.required')
      if (!data.city.trim()) errs.city = t('nhi.err.required')
    }
    if (n === 4) {
      if (!data.declare) errs.declare = t('nhi.err.declare')
      if (!data.dataProcessing) errs.dataProcessing = t('nhi.err.dataProcessing')
    }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  /* ---------------- identity verification sequence ---------------- */
  function runIdentityVerification() {
    setVerifying(true)
    setVerifyIdx(-1)
    const seq = [120, 740, 1360, 1980]
    seq.forEach((ms, i) => {
      timers.current.push(
        window.setTimeout(() => {
          setVerifyIdx(i)
          if (i === seq.length - 1) finishIdentityVerification()
        }, ms),
      )
    })
  }
  function finishIdentityVerification() {
    setVerifying(false)
    if (!forcedScenario && scenario === 'existing') {
      // With the API unavailable the demo 'existing' scenario resolves the
      // EMPI match with the prototype's pinned demo NHN. When the API is
      // live the outcome comes from the final submit instead; either way a
      // definite match never issues a second NHN.
      setData((prev) => ({ ...prev, identityVerified: true, assurance: 'IAL-3' }))
      setNhn('SY-4837-2916-052')
      setResultAssurance('IAL-3')
      setView('existing')
      return
    }
    setData((prev) => ({ ...prev, identityVerified: true, assurance: 'IAL-3' }))
    setStep(2)
    setMaxReached((m) => Math.max(m, 2))
  }

  /* ---------------- offline persistence (demo fallback) ---------------- */
  /**
   * Persist a successful NHI registration into the offline demo store so the
   * person becomes searchable in PatientSearch / ProviderExperience on the
   * static (API-less) demo — same entry shape PatientRegistration persists
   * via offlineStore.appendOfflineRegistration (entry.patient follows
   * PatientResponse; consumers map it through patientResponseToRosterRow and
   * resolve detail by patient.id).
   *
   * Called ONLY from the API-unreachable fallback: when the real API
   * responded successfully the backend owns the record and nothing is
   * double-written to localStorage.
   */
  function persistOfflineRegistration(nhn: string | null) {
    const nameLatin = [data.firstName, data.fatherName, data.familyName]
      .map((part) => part.trim())
      .filter(Boolean)
      .join(' ')
    // Issued NHN when available (the locally generated fallback one included)
    // so search → profile lookups resolve to the same person.
    const nhi = nhn ?? genLocalNhn()
    const personId = crypto.randomUUID()
    const patientId = crypto.randomUUID()
    const address = {
      ...(data.governorate ? { governorate: data.governorate } : {}),
      ...(data.city ? { city: data.city } : {}),
      ...(data.neighborhood ? { neighborhood: data.neighborhood } : {}),
      ...(data.street ? { street: data.street } : {}),
      ...(data.buildingNo ? { building_no: data.buildingNo } : {}),
    }
    // Extra informational fields beyond the OfflineRegistration contract —
    // kept optional so consumers that read known fields only are unaffected.
    const extras: Record<string, unknown> = {
      ...(data.nationalID && data.hasID === 'yes' ? { moi_id: data.nationalID } : {}),
      ...(Object.keys(address).length > 0 ? { address } : {}),
      registration_source: 'nhi-wizard',
    }
    const entry: OfflineRegistration = {
      patient: {
        id: patientId,
        person_id: personId,
        facility_id: 'fac-001',
        mrn: `MRN-${Date.now().toString().slice(-6)}`,
        verification_tier: data.identityVerified ? 3 : 1,
        status: 'active',
        person: {
          id: personId,
          nhi,
          name_ar: null,
          name_latin: nameLatin || null,
          dob: data.dob || null,
          sex: data.gender === 'male' ? 'M' : data.gender === 'female' ? 'F' : null,
          phone: data.mobile2 || null,
        },
        ...extras,
      },
      action: 'created_new',
      created_at: new Date().toISOString(),
    }
    appendOfflineRegistration(entry)
  }

  function buildPayload(): NhiRegisterPayload {
    return {
      hasID: data.hasID === 'yes',
      nationalID: data.hasID === 'yes' ? data.nationalID || undefined : undefined,
      firstName: data.firstName,
      fatherName: data.fatherName,
      familyName: data.familyName,
      motherName: data.motherName,
      gender: data.gender,
      dob: data.dob,
      placeOfBirth: data.placeOfBirth,
      civilGov: data.civilGov || undefined,
      placeOfReg: data.placeOfReg || undefined,
      civilRegNo: data.civilRegNo || undefined,
      maritalStatus: data.maritalStatus || undefined,
      docType: data.hasID === 'no' ? data.docType || undefined : undefined,
      docNumber: data.hasID === 'no' ? data.docNumber || undefined : undefined,
      docIssuer: data.hasID === 'no' ? data.docIssuer || undefined : undefined,
      mobile: data.mobile2 || undefined,
      email: data.email || undefined,
      governorate: data.governorate || undefined,
      city: data.city || undefined,
      neighborhood: data.neighborhood || undefined,
      street: data.street || undefined,
      buildingNo: data.buildingNo || undefined,
      otpVerified: data.otpVerified,
      identityVerified: data.identityVerified,
      declare: data.declare,
      dataProcessing: data.dataProcessing,
    }
  }

  function applyOutcome(outcome: NhiOutcome, res?: Partial<NhiRegisterResponse>) {
    if (outcome === 'existing') {
      setNhn(res?.nhn ?? 'SY-4837-2916-052')
      setResultAssurance(res?.assurance ?? data.assurance)
      setView('existing')
    } else if (outcome === 'pending_review') {
      setRefNumber(res?.reference_number ?? genLocalRef())
      setView('pendingReview')
    } else {
      setNhn(res?.nhn ?? genLocalNhn())
      setResultAssurance(res?.assurance ?? data.assurance)
      setView('success')
    }
  }

  async function finishSubmission() {
    setSubmitting(false)
    // When a scenario is pinned via the dev query param it drives the demo
    // outcome directly; otherwise call Dex's contract and render the
    // server-classified outcome, falling back to the chosen demo scenario if
    // the backend isn't reachable yet.
    try {
      if (forcedScenario && scenario !== 'new') {
        applyOutcome(scenario === 'duplicate' ? 'pending_review' : 'existing')
        return
      }
      const res = await postNhiRegister(buildPayload())
      applyOutcome(res.outcome, res)
    } catch {
      // Backend unreachable / static demo host: classify locally AND, when a
      // new NHN is issued (success), persist the registration to the offline
      // store so it shows up in PatientSearch. A successful real-API response
      // never reaches this branch, so no double-write to localStorage happens
      // when the backend owns the record; pending_review (possible duplicate)
      // issues no NHN and is likewise not persisted.
      if (scenario === 'duplicate') {
        applyOutcome('pending_review')
      } else {
        const demoNhn = genLocalNhn()
        persistOfflineRegistration(demoNhn)
        applyOutcome('success', { nhn: demoNhn })
      }
    }
  }

  function runFinalSubmission() {
    setSubmitting(true)
    setSubmitIdx(-1)
    const seq = [120, 670, 1220, 1770, 2320]
    seq.forEach((ms, i) => {
      timers.current.push(
        window.setTimeout(() => {
          setSubmitIdx(i)
          if (i === seq.length - 1) void finishSubmission()
        }, ms),
      )
    })
  }

  function onNext() {
    if (step === 1) {
      if (!validateStep(1)) return
      if (data.hasID === 'no') {
        setData((prev) => ({ ...prev, identityVerified: false, assurance: 'IAL-1' }))
        setStep(2)
        setMaxReached((m) => Math.max(m, 2))
        return
      }
      runIdentityVerification()
      return
    }
    if (step >= 2 && step <= 3) {
      if (!validateStep(step)) return
      const next = step + 1
      setStep(next)
      setMaxReached((m) => Math.max(m, next))
      return
    }
    if (step === 4) {
      if (!validateStep(4)) return
      runFinalSubmission()
    }
  }

  function resetApp() {
    clearTimers()
    setView('wizard')
    setStep(1)
    setMaxReached(1)
    setVerifying(false)
    setSubmitting(false)
    setVerifyIdx(-1)
    setSubmitIdx(-1)
    setNhn(null)
    setRefNumber(null)
    setResultAssurance(null)
    setErrors({})
    setData({ ...INITIAL_DATA })
    // "Go to Home" returns to the login screen — the entry point of the app.
    // Full page load (not client-side navigate) so the standalone-NHI check in
    // App() re-evaluates and the login page renders.
    window.location.href = '/'
  }

  /* ---------------- shared field renderer ---------------- */
  /* Field lives at module scope (see bottom of file). The context provider
     keeps a STABLE component identity so inputs never remount mid-typing. */
  const fieldContextValue = useMemo(
    () => ({ data, errors, setField }),
    [data, errors, setField],
  )

  const govOptions = GOVERNORATES.map((g) => ({ v: g, labelKey: `nhi.gov.${g}` }))

  /* ---------------- stepper ---------------- */
  function renderStepper() {
    if (view === 'existing' || view === 'pendingReview') return null
    const previewingSuccess = view === 'success'
    return (
      <nav className="nhi-stepper" aria-label={t('nhi.stepperAria')}>
        <ul className="nhi-step-list">
          {STEPPER_STEPS.map((s) => {
            const isLast = s.id === 5
            const active = isLast ? previewingSuccess : !previewingSuccess && s.id === step
            const done = isLast ? previewingSuccess : s.id < maxReached || previewingSuccess
            return (
              <li key={s.id}>
                <button
                  type="button"
                  className={`nhi-step-item ${active ? 'active' : ''} ${done ? 'done' : ''}`}
                  aria-current={active ? 'step' : undefined}
                  onClick={() => {
                    if (isLast) {
                      // Prototype preserved: the issued step can only preview
                      // once an NHN exists (otherwise generate the local one).
                      if (nhn || view === 'success') setView('success')
                    } else if (s.id <= maxReached) {
                      setView('wizard')
                      setStep(s.id)
                    }
                  }}
                >
                  <span className="nhi-step-num">{done && !active ? '✓' : s.id}</span>
                  <span className="nhi-step-text"><Bi k={s.key} /></span>
                </button>
              </li>
            )
          })}
        </ul>
      </nav>
    )
  }

  function VSteps({ labels, idx, testId }: { labels: string[]; idx: number; testId: string }) {
    return (
      <div className="nhi-verify-steps" data-testid={testId}>
        {labels.map((labelKey, i) => {
          const cls = i < idx ? 'done' : i === idx ? 'active' : ''
          return (
            <div key={labelKey} className={`nhi-vstep ${cls}`}>
              <div className="nhi-dot">{i === idx ? <div className="nhi-mini-spin" /> : null}</div>
              <Bi k={labelKey} />
            </div>
          )
        })}
      </div>
    )
  }

  function ScreenHead({ stepNo, titleKey, helperKey }: { stepNo: number; titleKey: string; helperKey: string }) {
    return (
      <div className="nhi-screen-head">
        <div className="nhi-eyebrow">{t('nhi.stepOf').replace('{n}', String(stepNo))}</div>
        <h1><Bi k={titleKey} /></h1>
        <p className="nhi-helper"><Bi k={helperKey} /></p>
      </div>
    )
  }

  /* ---------------- screens ---------------- */
  function screenPersonal() {
    const verified = data.identityVerified
    const nameBadge = verified ? (
      <div className="nhi-verified-badge">✓ {t('nhi.badge.civilVerified')}</div>
    ) : null
    const showId = data.hasID === 'yes'
    const showAltDocs = data.hasID === 'no'
    return (
      <>
        <ScreenHead
          stepNo={1}
          titleKey="nhi.s1.title"
          helperKey="nhi.s1.helper"
        />
        <div className="nhi-field-grid">
          <div
            className={`nhi-field ${errors.hasID ? 'invalid' : ''}`}
            data-testid="nhi-field-hasID"
          >
            <label>
              {t('nhi.hasId')}
              <span className="nhi-req">*</span>
            </label>
            <div className="nhi-radio-row">
              {(['yes', 'no'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  className={`nhi-pill ${data.hasID === v ? 'selected' : ''}`}
                  aria-pressed={data.hasID === v}
                  onClick={() => setField('hasID', v)}
                >
                  <Bi k={v === 'yes' ? 'nhi.yes' : 'nhi.no'} />
                </button>
              ))}
            </div>
            <span className="nhi-error-text">{errors.hasID ?? ''}</span>
          </div>
          {showId ? (
            <Field
              fieldKey="nationalID"
              labelKey="nhi.nationalId"
              required
              placeholderKey="nhi.ph.nationalId"
            />
          ) : null}
        </div>

        {showAltDocs ? (
          <>
            <div className="nhi-banner info" style={{ marginTop: 16 }}>
              <span>ℹ️</span>
              <span><Bi k="nhi.altDocBanner" /></span>
            </div>
            <div className="nhi-field-grid" style={{ marginTop: 14 }}>
              <Field
                fieldKey="docType"
                labelKey="nhi.docType"
                type="select"
                options={[
                  { v: 'passport', labelKey: 'nhi.doc.passport' },
                  { v: 'extract', labelKey: 'nhi.doc.extract' },
                  { v: 'other', labelKey: 'nhi.doc.other' },
                ]}
              />
              <Field fieldKey="docNumber" labelKey="nhi.docNumber" placeholderKey="nhi.ph.docNumber" />
              <Field fieldKey="docIssuer" labelKey="nhi.docIssuer" placeholderKey="nhi.ph.docIssuer" />
            </div>
          </>
        ) : null}

        <hr className="nhi-divider" />
        <div className="nhi-section-tag"><Bi k="nhi.s1.personalDetails" /></div>

        <div className="nhi-field-grid" style={{ marginTop: 12 }}>
          <Field
            fieldKey="familyName"
            labelKey="nhi.familyName"
            required
            readonly={verified}
            badge={nameBadge}
          />
          <Field fieldKey="firstName" labelKey="nhi.firstName" required readonly={verified} />
          <Field fieldKey="fatherName" labelKey="nhi.fatherName" required readonly={verified} />
          <Field
            fieldKey="motherName"
            labelKey="nhi.motherName"
            required
            placeholderKey="nhi.ph.motherName"
          />
          <Field
            fieldKey="gender"
            labelKey="nhi.gender"
            type="select"
            required
            readonly={verified}
            options={[
              { v: 'male', labelKey: 'nhi.gender.male' },
              { v: 'female', labelKey: 'nhi.gender.female' },
            ]}
          />
          <Field fieldKey="dob" labelKey="nhi.dob" type="date" required readonly={verified} />
          <Field
            fieldKey="placeOfBirth"
            labelKey="nhi.placeOfBirth"
            type="select"
            required
            options={govOptions}
          />
        </div>

        {verifying ? (
          <VSteps
            testId="nhi-verify-steps"
            idx={verifyIdx}
            labels={['nhi.vstep.civil', 'nhi.vstep.strongIds', 'nhi.vstep.empi']}
          />
        ) : null}
      </>
    )
  }

  function screenCivil() {
    return (
      <>
        <ScreenHead stepNo={2} titleKey="nhi.s2.title" helperKey="nhi.s2.helper" />
        <div className="nhi-field-grid">
          <Field
            fieldKey="civilGov"
            labelKey="nhi.civilGov"
            type="select"
            required
            options={govOptions}
          />
          <Field
            fieldKey="placeOfReg"
            labelKey="nhi.placeOfReg"
            placeholderKey="nhi.ph.placeOfReg"
          />
          <Field
            fieldKey="civilRegNo"
            labelKey="nhi.civilRegNo"
            placeholderKey="nhi.ph.civilRegNo"
          />
          <Field
            fieldKey="maritalStatus"
            labelKey="nhi.maritalStatus"
            type="select"
            options={[
              { v: 'single', labelKey: 'nhi.marital.single' },
              { v: 'married', labelKey: 'nhi.marital.married' },
              { v: 'divorced', labelKey: 'nhi.marital.divorced' },
              { v: 'widowed', labelKey: 'nhi.marital.widowed' },
            ]}
          />
        </div>
      </>
    )
  }

  function screenContact() {
    const otpBadge = data.otpVerified ? (
      <div className="nhi-verified-badge">✓ {t('nhi.badge.verified')}</div>
    ) : null
    return (
      <>
        <ScreenHead stepNo={3} titleKey="nhi.s3.title" helperKey="nhi.s3.helper" />

        <div className="nhi-field-grid">
          <Field fieldKey="email" labelKey="nhi.email" type="email" placeholderKey="nhi.ph.optional" />
        </div>

        <div className={`nhi-field ${errors.mobile2 ? 'invalid' : ''}`} style={{ marginTop: 16 }}>
          <label htmlFor="nhi-mobile2">{t('nhi.mobile')}</label>
          <div className="nhi-phone-row">
            <div className="nhi-phone-prefix">+963</div>
            <input
            dir="auto"
              id="nhi-mobile2"
              aria-label={t('nhi.mobile')}
              type="tel"
              value={data.mobile2}
              placeholder={t('nhi.ph.mobile')}
              readOnly={data.otpVerified}
              onChange={(e) => setField('mobile2', e.target.value)}
            />
            <button
              type="button"
              className="nhi-inline-btn"
              disabled={!data.mobile2.trim() || data.otpVerified}
              onClick={() => setData((prev) => ({ ...prev, otpSent: true }))}
            >
              {data.otpSent ? t('nhi.resend') : t('nhi.sendOtp')}
            </button>
          </div>
          {otpBadge}
          <span className="nhi-error-text">{errors.mobile2 ?? ''}</span>
        </div>

        {data.otpSent && !data.otpVerified ? (
          <div className="nhi-field" style={{ marginTop: 14 }}>
            <label htmlFor="nhi-otp">{t('nhi.otpCode')}</label>
            <div className="nhi-phone-row">
              <input
            dir="auto"
                id="nhi-otp"
                aria-label={t('nhi.otpCode')}
                type="text"
                maxLength={6}
                value={data.otp}
                placeholder="______"
                style={{ letterSpacing: 5, textAlign: 'center' }}
                onChange={(e) => setField('otp', e.target.value)}
              />
              <button
                type="button"
                className="nhi-inline-btn"
                disabled={data.otp.trim().length < 4}
                onClick={() => setData((prev) => ({ ...prev, otpVerified: true }))}
              >
                {t('nhi.verifyOtp')}
              </button>
            </div>
          </div>
        ) : null}

        <hr className="nhi-divider" />
        <div className="nhi-section-tag"><Bi k="nhi.s3.address" /></div>

        <div className="nhi-field-grid" style={{ marginTop: 12 }}>
          <Field
            fieldKey="governorate"
            labelKey="nhi.governorate"
            type="select"
            required
            options={govOptions}
          />
          <Field fieldKey="city" labelKey="nhi.city" required placeholderKey="nhi.ph.city" />
          <Field
            fieldKey="neighborhood"
            labelKey="nhi.neighborhood"
            placeholderKey="nhi.ph.neighborhood"
          />
          <Field fieldKey="street" labelKey="nhi.street" placeholderKey="nhi.ph.street" />
          <Field fieldKey="buildingNo" labelKey="nhi.buildingNo" placeholderKey="nhi.ph.buildingNo" />
        </div>
      </>
    )
  }

  function ReviewRow({ labelKey, value, testId }: { labelKey: string; value: string; testId?: string }) {
    return (
      <div className="nhi-review-row" data-testid={testId}>
        <div className="nhi-k">{t(labelKey)}</div>
        <div className={`nhi-v ${!value ? 'empty' : ''}`}>{value || t('nhi.notProvided')}</div>
      </div>
    )
  }

  function ReviewCard({ titleKey, stepId, children }: { titleKey: string; stepId: number; children: ReactNode }) {
    return (
      <div className="nhi-review-card">
        <div className="nhi-review-card-head">
          <div><Bi k={titleKey} /></div>
          <button
            type="button"
            className="nhi-review-edit"
            aria-label={t('nhi.editStepAria').replace('{title}', t(titleKey))}
            onClick={() => {
              setStep(stepId)
              setView('wizard')
            }}
          >
            {t('nhi.edit')} / {ar('nhi.edit')} ✎
          </button>
        </div>
        <div className="nhi-review-body">{children}</div>
      </div>
    )
  }

  function screenReview() {
    const genderLabel = data.gender === 'male' ? t('nhi.gender.male') : data.gender === 'female' ? t('nhi.gender.female') : ''
    return (
      <>
        <ScreenHead stepNo={4} titleKey="nhi.s4.title" helperKey="nhi.s4.helper" />

        {scenario === 'duplicate' ? (
          <div className="nhi-banner amber" data-testid="nhi-duplicate-banner">
            <span>⚠️</span>
            <span><Bi k="nhi.duplicateBanner" /></span>
          </div>
        ) : null}

        <ReviewCard titleKey="nhi.step.personal" stepId={1}>
          <ReviewRow
            labelKey="nhi.rv.fullName"
            value={[data.firstName, data.fatherName, data.familyName].filter(Boolean).join(' ')}
          />
          <ReviewRow labelKey="nhi.motherName" value={data.motherName} />
          <ReviewRow labelKey="nhi.gender" value={genderLabel} />
          <ReviewRow labelKey="nhi.dob" value={data.dob} />
          <ReviewRow labelKey="nhi.placeOfBirth" value={data.placeOfBirth} />
          <ReviewRow
            labelKey="nhi.nationalId"
            testId="nhi-rv-nationalId"
            value={data.hasID === 'yes' ? maskId(data.nationalID) : t('nhi.notProvided')}
          />
          <ReviewRow
            labelKey="nhi.rv.verificationStatus"
            testId="nhi-rv-idstatus"
            value={data.identityVerified ? t('nhi.verified') : t('nhi.unverified')}
          />
          {data.hasID === 'no' ? (
            <ReviewRow labelKey="nhi.docType" value={data.docType} />
          ) : null}
        </ReviewCard>

        <ReviewCard titleKey="nhi.step.civil" stepId={2}>
          <ReviewRow labelKey="nhi.rv.registryGov" value={data.civilGov} />
          <ReviewRow labelKey="nhi.civilRegNo" value={data.civilRegNo} />
        </ReviewCard>

        <ReviewCard titleKey="nhi.step.contact" stepId={3}>
          <ReviewRow
            labelKey="nhi.rv.mobileVerification"
            testId="nhi-rv-otp"
            value={data.otpVerified ? t('nhi.verified') : t('nhi.unverified')}
          />
          <ReviewRow labelKey="nhi.governorate" value={data.governorate} />
          <ReviewRow labelKey="nhi.rv.city" value={data.city} />
          <ReviewRow labelKey="nhi.street" value={data.street} />
          <ReviewRow labelKey="nhi.rv.buildingNo" value={data.buildingNo} />
        </ReviewCard>

        <div className={`nhi-checkbox-row ${errors.declare ? 'invalid' : ''}`}>
          <input
            dir="auto"
            type="checkbox"
            id="nhi-declare"
            checked={data.declare}
            onChange={(e) => setField('declare', e.target.checked)}
          />
          <label htmlFor="nhi-declare" className="nhi-ctext">
            <Bi k="nhi.declareText" />
          </label>
        </div>
        {errors.declare ? <span className="nhi-error-text">{errors.declare}</span> : null}
        <div className={`nhi-checkbox-row ${errors.dataProcessing ? 'invalid' : ''}`}>
          <input
            dir="auto"
            type="checkbox"
            id="nhi-dataProc"
            checked={data.dataProcessing}
            onChange={(e) => setField('dataProcessing', e.target.checked)}
          />
          <label htmlFor="nhi-dataProc" className="nhi-ctext">
            <Bi k="nhi.dataProcText" />
          </label>
        </div>
        {errors.dataProcessing ? <span className="nhi-error-text">{errors.dataProcessing}</span> : null}

        {submitting ? (
          <VSteps
            testId="nhi-submit-steps"
            idx={submitIdx}
            labels={[
              'nhi.sstep.docVerification',
              'nhi.sstep.empiSearch',
              'nhi.sstep.classify',
              scenario === 'duplicate' ? 'nhi.sstep.routeQueue' : 'nhi.sstep.generateNhn',
            ]}
          />
        ) : null}
      </>
    )
  }

  /* ---------------- result screens ---------------- */
  function screenExisting() {
    return (
      <div className="nhi-result-wrap" data-testid="nhi-result-existing">
        <div className="nhi-result-icon success">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#087A5B" strokeWidth="2.4">
            <path d="M9 12l2 2 4-4" />
            <circle cx="12" cy="12" r="9" />
          </svg>
        </div>
        <h1>{t('nhi.existing.title')}</h1>
        <p className="nhi-helper">{t('nhi.existing.helper')}</p>
        <div className="nhi-nhn-display">{nhn}</div>
        <div className="nhi-status-chip verified">● {t('nhi.verifiedChip')}</div>
        <div className="nhi-result-actions">
          <button type="button" className="nhi-btn nhi-btn-primary" onClick={() => window.print()}>
            🖨️ {t('nhi.printCert')}
          </button>
          <button type="button" className="nhi-btn nhi-btn-ghost" onClick={resetApp}>
            {t('nhi.goHome')}
          </button>
        </div>
        <p className="nhi-result-note">{t('nhi.existing.note')}</p>
      </div>
    )
  }

  function screenPendingReview() {
    return (
      <div className="nhi-result-wrap" data-testid="nhi-result-pending">
        <div className="nhi-result-icon pending">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#8A5A00" strokeWidth="2.4">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 3" />
          </svg>
        </div>
        <h1>{t('nhi.pending.title')}</h1>
        <p className="nhi-helper">{t('nhi.pending.helper')}</p>
        <div className="nhi-ref-display" data-testid="nhi-ref-number">
          {refNumber}
        </div>
        <div className="nhi-status-chip pending-status">● {t('nhi.pendingChip')}</div>
        <div className="nhi-result-actions">
          <button type="button" className="nhi-btn nhi-btn-ghost" onClick={resetApp}>
            {t('nhi.goHome')}
          </button>
        </div>
        <p className="nhi-result-note">{t('nhi.pending.note')}</p>
      </div>
    )
  }

  function screenSuccess() {
    const verified = data.identityVerified
    // resultAssurance from the server wins; else the wizard's own verification
    // state decides the chip (prototype parity).
    const chipVerified = resultAssurance ? resultAssurance !== 'IAL-1' : verified
    return (
      <div className="nhi-result-wrap" data-testid="nhi-result-success">
        <div className="nhi-result-icon success">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#087A5B" strokeWidth="2.4">
            <path d="M9 12l2 2 4-4" />
            <circle cx="12" cy="12" r="9" />
          </svg>
        </div>
        <h1>{t('nhi.success.title')}</h1>
        <p className="nhi-helper">{t('nhi.success.yourNhn')}</p>
        <div className="nhi-nhn-display" data-testid="nhi-nhn-value">
          {nhn}
        </div>
        <div className="nhi-qr-box">{nhn ? qrRects(nhn) : null}</div>
        <div className={`nhi-status-chip ${chipVerified ? 'verified' : 'basic'}`}>
          ● {chipVerified ? t('nhi.verifiedChip') : t('nhi.basicChip')}
        </div>
        <div className="nhi-result-actions">
          <button type="button" className="nhi-btn nhi-btn-primary" onClick={() => window.print()}>
            🖨️ {t('nhi.printCert')}
          </button>
          <button type="button" className="nhi-btn nhi-btn-ghost" onClick={() => window.print()}>
            ⬇️ {t('nhi.savePdf')}
          </button>
          <button type="button" className="nhi-btn nhi-btn-ghost" onClick={resetApp}>
            {t('nhi.goHome')}
          </button>
        </div>
        <p className="nhi-result-note">{t('nhi.success.permanence')}</p>
      </div>
    )
  }

  /* ---------------- footer ---------------- */
  function footer() {
    if (view !== 'wizard') return null
    const isFirst = step === 1
    let nextLabel = t('nhi.next')
    if (step === 1)
      nextLabel = data.hasID === 'no' ? t('nhi.continueNoId') : t('nhi.verifyContinue')
    if (step === 4) nextLabel = t('nhi.confirmReg')
    const busy = (step === 1 && verifying) || (step === 4 && submitting)
    return (
      <div className="nhi-panel-footer">
        <button
          type="button"
          className="nhi-btn nhi-btn-ghost"
          style={isFirst ? { visibility: 'hidden' } : undefined}
          onClick={() => {
            if (step > 1) setStep(step - 1)
          }}
        >
          {t('nhi.back')}
        </button>
        <button
          type="button"
          className="nhi-btn nhi-btn-primary"
          disabled={busy}
          onClick={onNext}
          data-testid="nhi-next"
        >
          {busy ? <span className="nhi-spinner" /> : null} {nextLabel}
        </button>
      </div>
    )
  }

  function screenForStep(): ReactNode {
    if (view === 'existing') return screenExisting()
    if (view === 'pendingReview') return screenPendingReview()
    if (view === 'success') return screenSuccess()
    switch (step) {
      case 1:
        return screenPersonal()
      case 2:
        return screenCivil()
      case 3:
        return screenContact()
      default:
        return screenReview()
    }
  }

  return (
    <NhiFieldContext.Provider value={fieldContextValue}>
    <div className="nhi-root" data-testid="nhi-wizard">
      <header className="nhi-topbar">
        <div className="nhi-brand">
          <svg width="30" height="30" viewBox="0 0 100 100" fill="none" aria-hidden="true">
            <circle cx="50" cy="50" r="48" fill="#0A2E27" stroke="#B69C5C" strokeWidth="2" />
            <path
              d="M50 26c-9 8-22 10-28 9 1 15 8 27 28 39 20-12 27-24 28-39-6 1-19-1-28-9z"
              fill="none"
              stroke="#B69C5C"
              strokeWidth="2.2"
            />
            <circle cx="50" cy="48" r="4.2" fill="#B69C5C" />
            <path
              d="M35 40c6-4 10-4 15-1M65 40c-6-4-10-4-15-1"
              stroke="#B69C5C"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
            />
            <circle cx="38" cy="24" r="2" fill="#B69C5C" />
            <circle cx="50" cy="19" r="2" fill="#B69C5C" />
            <circle cx="62" cy="24" r="2" fill="#B69C5C" />
          </svg>
          <div className="nhi-brand-text">
            <span className="nhi-brand-en">Syrian National Health Number</span>
            <span className="nhi-brand-ar" dir="rtl">تسجيل الهوية الصحية</span>
          </div>
        </div>
      </header>
      {forcedScenario !== null ? (
        <div className="nhi-dev-scenario" data-testid="nhi-dev-scenario">
          <span>{t('nhi.devScenario')}</span>
          <select
            aria-label={t('nhi.devScenario')}
            value={scenario}
            onChange={(e) => setScenario(e.target.value as NhiScenario)}
          >
            <option value="new">{t('nhi.scenario.new')}</option>
            <option value="existing">{t('nhi.scenario.existing')}</option>
            <option value="duplicate">{t('nhi.scenario.duplicate')}</option>
          </select>
        </div>
      ) : null}
      <div className="nhi-shell">
        {renderStepper()}
        <main className="nhi-panel">
          {screenForStep()}
          {footer()}
        </main>
      </div>
    </div>
    </NhiFieldContext.Provider>
  )
}

export default NhiRegistrationWizard

/* Field context — carries data/errors/setField to the top-level Field
   without threading props through 30 call sites. */
const NhiFieldContext = createContext<{
  data: NhiFormData
  errors: Record<string, string>
  setField: (key: keyof NhiFormData, value: NhiFormData[keyof NhiFormData]) => void
} | null>(null)

function useNhiFieldContext() {
  const ctx = useContext(NhiFieldContext)
  if (!ctx) throw new Error('Field must be rendered inside NhiFieldContext.Provider')
  return ctx
}

/* ============================================================
   Shared field renderer — defined OUTSIDE the wizard component.

   Previously this was an inline function inside NhiRegistrationWizard,
   which recreated the component identity on every keystroke. React then
   unmounted/remounted the inputs mid-typing → focus loss + IME
   composition garbling (Arabic "character errors"). Must stay a stable
   top-level component identity.
   ============================================================ */
function Field({
  fieldKey,
  labelKey,
  type = 'text',
  required = false,
  options,
  placeholderKey,
  readonly = false,
  badge,
}: {
  fieldKey: keyof NhiFormData
  labelKey: string
  type?: 'text' | 'tel' | 'email' | 'date' | 'select'
  required?: boolean
  options?: { v: string; labelKey: string }[]
  placeholderKey?: string
  readonly?: boolean
  badge?: ReactNode
}) {
  const { data, errors, setField } = useNhiFieldContext()
  const value = String(data[fieldKey] ?? '')
  const err = errors[fieldKey]
  return (
    <div
      className={`nhi-field ${err ? 'invalid' : ''}`}
      data-testid={`nhi-field-${String(fieldKey)}`}
    >
      <label htmlFor={`nhi-${String(fieldKey)}`}>
        <Bi k={labelKey} />
        {required ? <span className="nhi-req">*</span> : null}
      </label>
      {type === 'select' ? (
        <select
          id={`nhi-${String(fieldKey)}`}
          aria-label={t(labelKey)}
          value={value}
          disabled={readonly}
          onChange={(e) => setField(fieldKey, e.target.value)}
        >
          <option value="">{t('nhi.select')}</option>
          {(options ?? []).map((o) => (
            <option key={o.v} value={o.v}>
              {t(o.labelKey)} / {ar(o.labelKey)}
            </option>
          ))}
        </select>
      ) : (
        <input
          dir="auto"
          id={`nhi-${String(fieldKey)}`}
          aria-label={t(labelKey)}
          type={type}
          value={value}
          placeholder={placeholderKey ? t(placeholderKey) : ''}
          readOnly={readonly}
          onChange={(e) => setField(fieldKey, e.target.value)}
        />
      )}
      {badge ?? null}
      <span className="nhi-error-text">{err ?? ''}</span>
    </div>
  )
}
